/**
 * Booking-CTA UTM stamping for Calendly links, shared so web and mobile
 * attribute a booking to the same tracking surface with the same routing
 * rule, not two independently hand-copied implementations.
 *
 * Calendly forwards utm_* + salesforce_uuid from the booking-page URL into its
 * webhook payload (`payload.tracking`), which the growth-engine Calendly
 * receiver writes to the demo_bookings ledger. Stamping every in-app Calendly
 * href lets each booking attribute to the exact surface that produced it:
 *   utm_source=app · utm_medium=in_app · utm_campaign=booking-cta
 *   utm_content=<surface> (where) · utm_term=<experiment arm, when present>
 *   salesforce_uuid=<mongo userId> (deterministic user join even when the
 *   invitee books with a different email than their account)
 */

export type BookingSurface =
  | "topbar_pill"
  | "topbar_book_call"
  | "pricing"
  | "upload_direct_deposit"
  | "cancel_flow"
  | "cancel_offer"
  | "exit_intent"
  | "post_transcript_card"
  | "onboarding_complete"
  | "activation_complete"
  | "ai_chat"
  | "locked_feature"
  | "help_menu"
  | "paywall"
  // Launch bar splits its surface by copy arm rather than riding utm_term, which
  // already carries the site-wide booking-cta-copy arm. Keeping utm_term intact
  // means a launch booking still reports into that experiment while utm_content
  // separates which launch message produced it.
  | "launch_bar_a"
  | "launch_bar_b"
  // Reactivation welcome-back modal, one surface per cohort variant.
  | "welcome-modal"
  | "welcome-modal-active"
  // Enabled workspaces book for setup help, not to buy. Separate surface so support
  // bookings never inflate the acquisition read on launch_bar_a/_b.
  | "launch_bar_support"
  // In-app multimodal promos: the locked analysis-mode dropdown in AI chat and the
  // media-insight promos under the video player / above the audio controls. One
  // surface per placement so the demo_bookings ledger reads each independently.
  | "chat_analysis_locked"
  | "media_video_promo"
  | "media_audio_promo"
  // Locked analysis-input control in the automation builder — same upsell as the
  // chat dropdown, its own surface so builder-driven bookings read separately.
  | "automation_analysis_locked"
  // Mobile's Profile screen — always visible, unlike topbar_book_call's
  // salesQualified gate on web.
  | "mobile_profile"
  // The desktop recorder's Help menu, tray and About section.
  | "desktop_help"
  | "desktop_after_meetings";

interface BookingUtmOptions {
  userId?: string;
  /** Experiment arm identity — lands in utm_term per the site-wide convention. */
  arm?: string;
}

/**
 * customer-meeting: paying accounts. consult: business-domain prospects (the demo).
 * free-call: free-mail prospects, whose calendar asks more qualifying questions.
 */
export type BookingCalendarTarget = "customer-meeting" | "consult" | "free-call";

const BOOKING_CALENDAR_URLS: Record<BookingCalendarTarget, string> = {
  "customer-meeting": "https://calendly.com/speak-ai/customer-meeting",
  consult: "https://calendly.com/speak-ai/consult",
  "free-call": "https://calendly.com/speak-ai/speak-ai-free-call",
};

/** Customer-only event types with no prospect equivalent, so the router never picks them. */
export type CustomerSessionTarget = "cancellation-session" | "success-session";

const CUSTOMER_SESSION_URLS: Record<CustomerSessionTarget, string> = {
  "cancellation-session": "https://calendly.com/speak-ai/cancellation-session",
  "success-session": "https://calendly.com/speak-ai/success-session",
};

/** Absolute Calendly URL for a customer-only session; stamp it with `withBookingUtms`. */
export function customerSessionUrl(target: CustomerSessionTarget): string {
  return CUSTOMER_SESSION_URLS[target];
}

/**
 * Surfaces that always book the consult calendar, regardless of subscription
 * state. The locked multimodal upsell sells a capability the workspace does
 * not have yet, so even a paying customer books a consult, not the account team.
 */
const CONSULT_ONLY_SURFACES: ReadonlySet<BookingSurface> = new Set([
  "chat_analysis_locked",
  "automation_analysis_locked",
]);

/** Whether this surface must route to the consult calendar for every account. */
export function isConsultOnlySurface(surface: BookingSurface): boolean {
  return CONSULT_ONLY_SURFACES.has(surface);
}

/**
 * Pick the calendar from the two facts that decide it: paying accounts get the
 * account team, business-domain prospects get the consult (demo) calendar, and
 * everyone else (free-mail, or domain not known) gets the free-call calendar.
 * Business means `isCompanyDomain === true` only; speak-server's `isBusinessUser`
 * (lifecycle emails) also adds lead score >= 40, which the client does not have.
 */
export function routeBookingTarget(
  isPaidActive: boolean,
  isCompanyDomain?: boolean | null,
): BookingCalendarTarget {
  if (isPaidActive) return "customer-meeting";
  return isCompanyDomain === true ? "consult" : "free-call";
}

/**
 * Route a booking CTA to the right Calendly calendar from the user's
 * subscription and email-domain class. Single source of truth for every in-app
 * booking surface.
 */
export function bookingCalendarTarget(
  subscription?: { isFree?: boolean; isExpired?: boolean } | null,
  isCompanyDomain?: boolean | null,
): BookingCalendarTarget {
  const isPaidActive = subscription ? !subscription.isFree && !subscription.isExpired : false;
  return routeBookingTarget(isPaidActive, isCompanyDomain);
}

/** Absolute Calendly URL for a booking calendar target. */
export function bookingCalendarUrl(target: BookingCalendarTarget): string {
  return BOOKING_CALENDAR_URLS[target];
}

/**
 * Return `baseUrl` with booking-attribution params appended. Existing query
 * params on the base URL are preserved. Returns the input unchanged if it is
 * not parseable as an absolute URL.
 */
export function withBookingUtms(
  baseUrl: string,
  surface: BookingSurface,
  options: BookingUtmOptions = {},
): string {
  let url: URL;
  try {
    url = new URL(baseUrl);
  } catch {
    return baseUrl;
  }
  url.searchParams.set("utm_source", "app");
  url.searchParams.set("utm_medium", "in_app");
  url.searchParams.set("utm_campaign", "booking-cta");
  url.searchParams.set("utm_content", surface);
  if (options.arm) {
    url.searchParams.set("utm_term", options.arm);
  }
  if (options.userId) {
    // carries the Mongo userId for the demo_bookings join; distinct from the "web:<session id>" token captured at signup (app.tsx)
    url.searchParams.set("salesforce_uuid", options.userId);
  }
  return url.toString();
}
